/* eslint-disable no-unused-expressions */
import { useContext, useState, useMemo, useEffect } from 'react'

import { makeStyles } from '@material-ui/core'

import useAddress from '../hooks/useAddress'
import { Address } from '../models/address.model'

import { AddressType } from '~/address/models/address.model'
import { throttle, matchHighlightString } from '~/core/helpers/helpers'
import { AppDepsContext } from '~/core/hooks/useAppDepsContext'

const useStyles = makeStyles((theme) => ({
  icon: {
    color: theme.palette.text.secondary,
    marginRight: theme.spacing(2),
  },
  bold: {
    fontWeight: 800,
  },
  normal: {
    fontWeight: 400,
  },
}))

export default function useAddressInput(id: string, addressType: AddressType) {
  const ctx = useContext(AppDepsContext)
  const classes = useStyles()
  const { address: value, setAddress, resetAddress } = useAddress(id, addressType)
  const [inputValue, setInputValue] = useState('')
  const [options, setOptions] = useState<Address[]>([])
  const [isLoading, setIsLoading] = useState(false)
  const [matches, setMatches] = useState<unknown[]>([])

  const fetchAddress = useMemo(
    () => async (input: string, fn: (results?: Address[]) => void): Promise<void> => {
      throttle(fn(await ctx?.addressService.fetchStreets(input)), 200)
    },
    [ctx],
  )

  useEffect(() => {
    let active = true

    if (inputValue === '') {
      setOptions(value ? [value] : [])
      return undefined
    }

    setIsLoading(true)
    fetchAddress(inputValue, (results) => {
      if (active) {
        let newOptions = [] as Address[]

        if (value) {
          newOptions = [value]
        }
        if (results) {
          newOptions = [...newOptions, ...results]
        }
        const defaultOption = {
          gescal: '',
          number: '',
          postalCode: '',
          province: '',
          provinceId: '',
          streetName: '',
          streetType: '',
          summary: inputValue,
          town: '',
        }
        setOptions([...newOptions, defaultOption])
        results?.forEach((result) =>
          setMatches((_matches) => [
            ..._matches,
            matchHighlightString(result?.description, inputValue),
          ]),
        )

        setIsLoading(false)
      }
    })

    return () => {
      active = false
    }
  }, [value, inputValue, fetchAddress])

  return {
    options,
    classes,
    isLoading,
    value,
    inputValue,
    matches,
    setInputValue,
    setOptions,
    setAddress,
    resetAddress,
  }
}
